import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Collapse, List, ListItem, ListItemButton, ListItemText, Paper, Typography } from '@mui/material';
import React, { useState } from 'react';
import type { Class, Student, YearGroup } from '../../types';

interface YearGroupTreeProps {
  yearGroups: YearGroup[];
  classes: Class[];
  students: Student[];
  onYearGroupSelect?: (yearGroup: YearGroup) => void;
  onClassSelect?: (classData: Class) => void;
  onStudentSelect?: (student: Student) => void;
}

export const YearGroupTree: React.FC<YearGroupTreeProps> = ({
  yearGroups,
  classes,
  students,
  onYearGroupSelect,
  onClassSelect,
  onStudentSelect,
}) => {
  const [expandedYearGroups, setExpandedYearGroups] = useState<Set<string>>(new Set());
  const [expandedClasses, setExpandedClasses] = useState<Set<string>>(new Set());

  const toggleYearGroup = (yearGroupId: string) => {
    const newExpanded = new Set(expandedYearGroups);
    if (newExpanded.has(yearGroupId)) {
      newExpanded.delete(yearGroupId);
    } else {
      newExpanded.add(yearGroupId);
    }
    setExpandedYearGroups(newExpanded);
  };

  const toggleClass = (classId: string) => {
    const newExpanded = new Set(expandedClasses);
    if (newExpanded.has(classId)) {
      newExpanded.delete(classId);
    } else {
      newExpanded.add(classId);
    }
    setExpandedClasses(newExpanded);
  };

  const getClassesForYearGroup = (yearGroupId: string) => {
    return classes.filter((c) => c.yearGroupId === yearGroupId);
  };

  const getStudentsForClass = (classId: string) => {
    return students.filter((s) => s.classId === classId);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Year Groups & Classes
      </Typography>
      <List>
        {yearGroups.map((yearGroup) => {
          const isYearGroupExpanded = expandedYearGroups.has(yearGroup.id);
          const yearGroupClasses = getClassesForYearGroup(yearGroup.id);

          return (
            <Box key={yearGroup.id}>
              <ListItemButton
                onClick={() => {
                  toggleYearGroup(yearGroup.id);
                  onYearGroupSelect?.(yearGroup);
                }}
              >
                {isYearGroupExpanded ? <ExpandLess /> : <ExpandMore />}
                <ListItemText
                  primary={yearGroup.name}
                  secondary={`${yearGroup.currentSize}/${yearGroup.capacity} students | ${yearGroupClasses.length} classes`}
                />
              </ListItemButton>
              <Collapse in={isYearGroupExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {yearGroupClasses.map((classData) => {
                    const isClassExpanded = expandedClasses.has(classData.id);
                    const classStudents = getStudentsForClass(classData.id);

                    return (
                      <Box key={classData.id}>
                        <ListItemButton
                          sx={{ pl: 4 }}
                          onClick={() => {
                            toggleClass(classData.id);
                            onClassSelect?.(classData);
                          }}
                        >
                          {isClassExpanded ? <ExpandLess /> : <ExpandMore />}
                          <ListItemText
                            primary={classData.name}
                            secondary={`${classData.currentSize}/${classData.capacity} students`}
                          />
                        </ListItemButton>
                        <Collapse in={isClassExpanded} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {classStudents.length === 0 ? (
                              <ListItem sx={{ pl: 8 }}>
                                <ListItemText secondary="No students assigned" />
                              </ListItem>
                            ) : (
                              classStudents.map((student) => (
                                <ListItemButton
                                  key={student.id}
                                  sx={{ pl: 8 }}
                                  onClick={() => onStudentSelect?.(student)}
                                >
                                  <ListItemText
                                    primary={`${student.firstName} ${student.lastName}`}
                                    secondary={student.admissionNumber}
                                  />
                                </ListItemButton>
                              ))
                            )}
                          </List>
                        </Collapse>
                      </Box>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>
    </Paper>
  );
};
